import React, { useCallback, useState, useEffect } from 'react';
import { graphql, withApollo } from 'react-apollo';
import { compose, mapProps } from 'recompose';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import InputField from 'components/common/InputField/InputField';
import { toUTC, toFormattedLocalDateAnswer } from 'utils/dateTime/converter';
import { NO_CACHE } from 'graphql/fetchPolicies';
import { getObjValue, getDateTime } from 'utils/functions';
import { createDateInputValidationFn } from 'utils/validations/validations';
import { useDebouncedStartTyping, useDebouncedStopTyping } from 'components/threepp/assessment/Answers/helper';
import { DateValues } from 'utils/constants';
import {
  updateDateFieldValue,
  userStartTyping as userStartTypingMut,
  userStopTyping as userStopTypingMut,
  getLatestAssessmentEventById,
  ddeActionsTriggered
} from 'graphql/operations';
import SaveAnswerHOC from '../SaveAnswerHOC';
import EventHandlerHOC from '../FieldUpdatesHOC';

import './DateAnswer.scss';

export function DateAnswer(props) {
  const { save, disabled, question, latestEvent, assessmentId, userStartTyping, userStopTyping } = props;
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  // convert to local time
  const localTimeValue = question.dateValue ? toFormattedLocalDateAnswer(question.dateValue, DateValues.DATE_FORMAT) : '';
  const { calculated } = question;
  const action = getObjValue(latestEvent, 'action');
  const isDisabled = disabled || action === 'START_TYPING';

  const userStartTypingCallback = useDebouncedStartTyping(userStartTyping, assessmentId, question.id);
  const userStopTypingCallback = useDebouncedStopTyping(userStopTyping, assessmentId, question.id);

  const inputValidation = createDateInputValidationFn(DateValues.MIN_DATE, DateValues.MAX_DATE);

  const handleChangeCallback = useCallback(
    input => {
      userStartTypingCallback();
      userStopTypingCallback();
      const normalizedValue = input ? toUTC(getDateTime(input)) : null;
      save(normalizedValue);
    },
    [save, userStartTypingCallback, userStopTypingCallback]
  );

  const handleValidationErrorCallback = useCallback(
    e => {
      const normalized = e.value === '' ? null : e.value;
      save(normalized);
    },
    [save]
  );

  useEffect(() => {
    const errorObj = localTimeValue ? inputValidation(localTimeValue.toString()) : null;
    setError(errorObj ? t(errorObj.key, { ...errorObj.args }) : null);
  }, [t, inputValidation, localTimeValue]);

  return (
    <InputField
      t={t}
      classes="text date-field-answer"
      type="date"
      name="answer"
      fullWidth
      disabled={calculated || isDisabled}
      value={localTimeValue}
      onChange={handleChangeCallback}
      inputValidation={inputValidation}
      onValidationError={handleValidationErrorCallback}
      error={error}
    />
  );
}

DateAnswer.defaultProps = {
  latestEvent: { action: '' }
};

DateAnswer.propTypes = {
  // TODO: Fix this...
  /* eslint-disable react/forbid-prop-types */
  question: PropTypes.object.isRequired,
  save: PropTypes.any.isRequired,
  disabled: PropTypes.bool.isRequired,
  userStartTyping: PropTypes.func.isRequired,
  userStopTyping: PropTypes.func.isRequired,
  latestEvent: PropTypes.object
  /* eslint-enable react/forbid-prop-types */
};

export const DateAnswerHOC = compose(
  withApollo,
  graphql(updateDateFieldValue, { name: 'fieldMutation' }),
  graphql(userStartTypingMut, { name: 'userStartTyping' }),
  graphql(userStopTypingMut, { name: 'userStopTyping' }),
  graphql(ddeActionsTriggered, { name: 'ddeActionsTriggered' }),
  graphql(getLatestAssessmentEventById, {
    options: props => ({
      variables: {
        fieldId: props.question.id
      },
      fetchPolicy: NO_CACHE
    }),
    name: 'getLatestAssessmentEventById'
  }),
  SaveAnswerHOC,
  EventHandlerHOC,
  mapProps(props => ({
    assessmentId: props.assessmentId,
    question: props.question,
    save: props.save,
    invokeActionsForFields: props.invokeActionsForFields,
    disabled: props.disabled,
    userStartTyping: props.userStartTyping,
    userStopTyping: props.userStopTyping,
    ddeActionsTriggered: props.ddeActionsTriggered,
    latestEvent: props.getLatestAssessmentEventById.getLatestAssessmentEventById,
    setDateValue: props.setDateValue
  }))
);

export default DateAnswerHOC(DateAnswer);
